import { useEffect, useMemo, useState } from 'react';
import firebase from 'lib/firebase';

const readBrowserOnlineState = () => (
  typeof navigator === 'undefined' ? true : navigator.onLine !== false
);

/**
 * Browser connectivity is advisory. Firestore performs its own reconnects, so
 * a document read must never gate authentication or the public application shell.
 */
export const useConnection = () => {
  const [isOnline, setIsOnline] = useState(readBrowserOnlineState);
  const isFirebaseReady = Boolean(firebase?.isConfigured && firebase?.db);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const status = useMemo(() => ({
    isOnline,
    isFirebaseReady,
    // Retry controls should remain available whenever the browser has a network.
    // The operation itself remains responsible for reporting a Firebase failure.
    isConnected: isOnline,
    retryCount: 0,
  }), [isFirebaseReady, isOnline]);

  return status;
};
